import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface ClaimSummary {
  readonly claimId: string;
  readonly claimNumber: string;
  readonly status: string;
  readonly totalClaimAmount: number;
  readonly billingPeriodFrom: string;
  readonly billingPeriodTo: string;
}

/** L13.04 — Vendor Claims List: all historical claims for this vendor. */
export function VendorBillingClaimsPage(): ReactNode {
  const { token } = useAuth();
  const [claims, setClaims] = useState<readonly ClaimSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('ALL');

  useEffect(() => {
    let alive = true;
    const qs = statusFilter === 'ALL' ? '' : `?status=${statusFilter}`;
    apiRequest<{ items: readonly ClaimSummary[] }>(`/v1/vendor/billing/claims${qs}`, { token })
      .then(r => { if (alive) setClaims(r.items); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token, statusFilter]);

  return (
    <div style={{ padding: 24 }}>
      <h1>All Claims</h1>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        {['ALL', 'DRAFT', 'SUBMITTED', 'APPROVED_FOR_FINANCE', 'RETURNED', 'REJECTED', 'WITHDRAWN'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: '4px 12px', border: '1px solid #d0d7de', borderRadius: 4, cursor: 'pointer', background: statusFilter === s ? '#0969da' : '#fff', color: statusFilter === s ? '#fff' : '#24292f' }}>{s}</button>
        ))}
      </div>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!claims && !error && <p>Loading...</p>}
      {claims && claims.length === 0 && <p>No claims match the filter.</p>}
      {claims && claims.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', borderBottom: '1px solid #d0d7de' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Claim #</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Period</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Amount (₹)</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Status</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Invoice</th>
            </tr>
          </thead>
          <tbody>
            {claims.map((c, i) => (
              <tr key={c.claimId ?? i} style={{ borderBottom: '1px solid #d0d7de' }}>
                <td style={{ padding: 8 }}><Link to={`/vendor/portal/billing/${c.claimId}`} style={{ color: '#0969da' }}>{c.claimNumber}</Link></td>
                <td style={{ padding: 8 }}>{c.billingPeriodFrom} – {c.billingPeriodTo}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{(c.totalClaimAmount ?? 0).toLocaleString('en-IN')}</td>
                <td style={{ padding: 8 }}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: c.status === 'APPROVED_FOR_FINANCE' ? '#dafbe1' : c.status === 'REJECTED' ? '#ffebe9' : '#fff8c5' }}>{c.status}</span></td>
                <td style={{ padding: 8 }}><Link to={`/vendor/portal/billing/${c.claimId}/invoice`} style={{ color: '#0969da' }}>View Bill</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
